/** Picks the first mimeType the browser reports as supported, in priority order. */
export const selectSupportedMimeType = (
  candidates: string[],
  isTypeSupported: (mimeType: string) => boolean,
): string | undefined => {
  for (const candidate of candidates) {
    if (isTypeSupported(candidate)) {
      return candidate;
    }
  }
  return undefined;
};

/** Turns a MediaRecorder error name into the message shown to the user. */
export const getRecordingErrorMessage = (errorName: string): string => {
  if (errorName === "UnknownError" || errorName === "Restricted") {
    return "Recording error. If using Firefox, please try Chrome or Safari instead.";
  }
  return `Recording error: ${errorName}. Try using Chrome or Safari.`;
};

/** Builds the ffmpeg CLI args used to transcode a recorded WebM clip to a shareable MP4. */
export const buildMp4ConversionArgs = (
  inputFilename: string,
  outputFilename: string,
  maxWidth: number,
  fps: number,
): string[] => [
  "-i",
  inputFilename,
  "-vf",
  `scale='min(${maxWidth},iw)':-2,fps=${fps}`,
  "-c:v",
  "libx264",
  "-preset",
  "ultrafast",
  "-crf",
  "28",
  "-c:a",
  "aac",
  "-b:a",
  "96k",
  "-ar",
  "44100",
  "-movflags",
  "+faststart",
  "-pix_fmt",
  "yuv420p",
  outputFilename,
];

/* ── Note-event timeline ──────────────────────────────────────────────────── */

export interface NoteEvent {
  note: number;
  type: "on" | "off";
  timeMs: number;
}

/**
 * Note events are collected from a rAF-driven observer, so the array is
 * *usually* in chronological order — but nothing enforces it, and any consumer
 * that scans with an early `break` silently truncates the timeline if it isn't.
 * Sorting up-front makes that assumption explicit and cheap to guarantee.
 */
export const sortNoteEvents = (events: readonly NoteEvent[]): NoteEvent[] =>
  [...events].sort((a, b) => a.timeMs - b.timeMs);

/** The set of notes held down at `timeMs`, given a chronologically sorted timeline. */
export const activeNotesAt = (
  sortedEvents: readonly NoteEvent[],
  timeMs: number,
): Set<number> => {
  const active = new Set<number>();
  for (const event of sortedEvents) {
    if (event.timeMs > timeMs) break;
    if (event.type === "on") {
      active.add(event.note);
    } else {
      active.delete(event.note);
    }
  }
  return active;
};

/**
 * Appends a note-off for every note still held when capture ended. Without this
 * a note whose release lands after the recorder shut down stays "pressed"
 * forever in the rendered effects pass.
 */
export const closeOpenNotes = (
  sortedEvents: readonly NoteEvent[],
  endTimeMs: number,
): NoteEvent[] => {
  const closed = [...sortedEvents];
  activeNotesAt(sortedEvents, endTimeMs).forEach((note) => {
    closed.push({ note, type: "off", timeMs: endTimeMs });
  });
  return closed;
};

/* ── Duration / progress ──────────────────────────────────────────────────── */

/**
 * `HTMLMediaElement.duration` is `Infinity` for a Blob produced by MediaRecorder
 * (the WebM header carries no duration), and `NaN` before metadata loads. Both
 * poison any arithmetic done with it, so fall back to a measured value.
 */
export const resolveMediaDurationMs = (
  rawDurationSeconds: number,
  fallbackMs: number,
): number => {
  if (Number.isFinite(rawDurationSeconds) && rawDurationSeconds > 0) {
    return rawDurationSeconds * 1000;
  }
  return Number.isFinite(fallbackMs) && fallbackMs > 0 ? fallbackMs : 0;
};

/** Clamped, always-finite percentage for progress UI. */
export const computeProgressPercent = (
  currentMs: number,
  durationMs: number,
  max: number = 99,
): number => {
  if (!Number.isFinite(currentMs) || !Number.isFinite(durationMs)) return 0;
  if (durationMs <= 0 || currentMs <= 0) return 0;
  return Math.max(0, Math.min(max, Math.round((currentMs / durationMs) * 100)));
};

/**
 * Turns ffmpeg.wasm's `progress` ratio into a percentage, or `null` when the
 * number cannot be trusted.
 *
 * ffmpeg derives the ratio from the input's declared duration, and a
 * MediaRecorder WebM carries no duration in its header (the same gap
 * `resolveMediaDurationMs` works around). So for our own clips the ratio is
 * routinely 0, NaN, Infinity or far past 1. Reporting `null` for those lets the
 * UI say "converting" instead of inventing a number that does not track
 * anything real.
 */
export const normalizeFfmpegProgress = (
  progressRatio: number,
): number | null => {
  if (!Number.isFinite(progressRatio) || progressRatio <= 0) return null;
  // A ratio well past 1 means ffmpeg is working from a bogus duration.
  if (progressRatio > 1.5) return null;
  return Math.max(1, Math.min(99, Math.round(progressRatio * 100)));
};
